import {
  FocusTrap,
  Group,
  Paper,
  ScrollArea,
  Stack,
  Text,
} from '@mantine/core';

import { IconArrowLeft } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { NAVBAR_HEIGHT } from '../../../shared/components/UI/Navbar/Navbar';

import { PreviewSidebar } from './PreviewSidebar';
import type { useSearchTendersPreviewQuery } from './forms/hooks/streams.hook';
import type { SearchTendersPreviewQueryKey } from './forms/hooks/useStreamForm';

type EditStreamLayoutProps = {
  handleBackNav: () => void;
  children: React.ReactNode;
  previewQuery: ReturnType<typeof useSearchTendersPreviewQuery>;
  footerCTA: React.ReactElement;
  queryKeys: SearchTendersPreviewQueryKey[];
};

export function EditStreamLayout({
  handleBackNav,
  children,
  previewQuery,
  footerCTA,
  queryKeys,
}: EditStreamLayoutProps) {
  return (
    <Stack spacing={0} mah={`calc(100vh - ${NAVBAR_HEIGHT}px)`} h="100%">
      <ScrollArea
        sx={theme => ({
          flex: 1,
          background: theme.other.gradients.backgroundLight,
        })}
      >
        <FormContainer
          handleBackNav={handleBackNav}
          previewQuery={previewQuery}
          queryKeys={queryKeys}
        >
          {children}
        </FormContainer>
      </ScrollArea>
      <Footer
        isPreviewLoading={previewQuery.isLoading}
        resultsCount={previewQuery.data?.totalCount || 0}
      >
        {footerCTA}
      </Footer>
    </Stack>
  );
}

function BackNavButton({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation('stream');

  return (
    <Group position="left" noWrap>
      <Group
        noWrap
        spacing="02"
        onClick={onClick}
        color="gray.6"
        sx={{ cursor: 'pointer' }}
      >
        <IconArrowLeft size={16} />
        <Text variant="sm" fw={500}>
          {t('editStreamForm.backToResults')}
        </Text>
      </Group>
    </Group>
  );
}

type FormContainerProps = Pick<
  EditStreamLayoutProps,
  'handleBackNav' | 'previewQuery' | 'queryKeys'
> & {
  children: React.ReactNode;
};

function FormContainer({
  handleBackNav,
  previewQuery,
  queryKeys,
  children,
}: FormContainerProps) {
  return (
    <Stack px="07" pt="04" maw={1360} mx="auto" spacing="04">
      <BackNavButton onClick={handleBackNav} />

      <Group noWrap spacing="04" pb="04" align="flex-start">
        <Paper
          radius="md"
          p="06"
          w="75%"
          sx={theme => ({
            overflow: 'auto',
            border: `1px solid ${theme.colors.gray[1]}`,
            boxShadow:
              '0px 4px 12px -2px rgba(0, 0, 0, 0.04), 0px 2px 4px -2px rgba(0, 0, 0, 0.04)',
          })}
        >
          <FocusTrap active>
            <Stack spacing={32}>{children}</Stack>
          </FocusTrap>
        </Paper>

        <PreviewSidebar previewQuery={previewQuery} queryKeys={queryKeys} />
      </Group>
    </Stack>
  );
}

function Footer({
  isPreviewLoading,
  resultsCount,
  children,
}: {
  isPreviewLoading: boolean;
  resultsCount: number;
  children: React.ReactNode;
}) {
  const { t } = useTranslation('stream');

  return (
    <Group
      p="03"
      spacing="02"
      position="right"
      align="center"
      bg="white"
      sx={{
        zIndex: 0,
        boxShadow: `0px 1px 2px 0px rgba(0, 0, 0, 0.30), 0px 2px 6px 2px rgba(0, 0, 0, 0.15);`,
      }}
    >
      <Group noWrap align="center" p="03" spacing="01">
        <Text size="sm" fw={700}>
          {isPreviewLoading ? '...' : resultsCount}
        </Text>
        <Text size="sm" fw={400}>
          {t('editStreamForm.pendingOpportunities')}
        </Text>
      </Group>
      {children}
    </Group>
  );
}
